import { Injectable } from '@angular/core'
import { BehaviorSubject, merge, Observable, of } from 'rxjs'
import { FALLBACK_PERMISSIONS } from 'src/environments/go-api-fallbacks'
import { environment } from 'src/environments/environment'
import { catchError, distinctUntilChanged, filter, first, map, share, shareReplay, skipWhile, switchMap, tap } from 'rxjs/operators'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone' // dependent on utc plugin
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import duration from 'dayjs/plugin/duration'
import weekday from 'dayjs/plugin/weekday'
import '@core/helpers/chart-js-adapter-dayjs'

import { User } from '@shared/models/user.model'
import { JwtService } from './jwt.service'
import { ApiService } from '@shared/services/api.service'
import { PermissionsService } from '@shared/services/permissions.service'
import { GoRealtimeClient } from '@core/services/realtime/go-realtime.client'
import { negativeBoolean } from '@core/helpers/negative-boolean'
import { withFeedback } from '@core/interceptors/feedback-context'
import { AuthResponse, LoginResponse, TermOfUse, LogoutResponse } from './auth-response.model'
import { difference } from 'lodash-es'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(duration)
dayjs.extend(weekday)

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserEmail: string
  private currentUserBlockingMessage: string
  private currentUserPhone: string
  private currentUserDateFormat: string

  private isRememberMe: boolean

  private currentUserSource = new BehaviorSubject<User>(null)
  currentUser$ = this.currentUserSource.asObservable().pipe(
    skipWhile((user) => !user),
    distinctUntilChanged(),
  )

  redirectUrl: string
  isLoggedIn = false

  private isLoggedInSource = new BehaviorSubject<boolean | null>(null)
  isLoggedIn$ = this.isLoggedInSource.asObservable().pipe(skipWhile((isLoggedIn) => isLoggedIn === null))

  termsAgreementNeeded = false

  termsAcceptedSource = new BehaviorSubject<boolean | null>(null)
  termsAccepted$ = this.termsAcceptedSource.asObservable().pipe(skipWhile((termsAccepted) => termsAccepted === null))

  private isImpersonatedSource = new BehaviorSubject<boolean>(false)
  isImpersonated$ = this.isImpersonatedSource.asObservable()

  constructor(
    private jwtService: JwtService,
    private apiService: ApiService,
    private permissionsService: PermissionsService,
    private goRealtime: GoRealtimeClient,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  populate(): Observable<any> {
    if (!this.jwtService.getToken()) {
      this.purgeAuth()
      return of(null)
    }

    const user$ = this.getUser().pipe(shareReplay(), first())
    const impersonation$ = user$.pipe(
      switchMap(() => this.getImpersonation()),
      share(),
    )

    const ownUser$ = impersonation$.pipe(
      filter(negativeBoolean),
      switchMap(() => user$),
    )
    const impersonatedUser$ = impersonation$.pipe(
      filter(Boolean),
      tap((authData: AuthResponse) => {
        this.jwtService.setStorage('session')
        this.jwtService.saveToken(authData.token)
      }),
      switchMap(() => this.getUser(['billingPlanId'])),
      tap(() => this.isImpersonatedSource.next(true)),
    )

    return merge(ownUser$, impersonatedUser$).pipe(
      tap((user) => {
        this.termsAgreementNeeded = !this.isImpersonatedSource.getValue() && user.userTermsAcceptance
        !this.termsAgreementNeeded && this.termsAcceptedSource.next(true)

        this.isLoggedIn = true
        this.setCurrentUser(user)
        this.setCurrentUserDateFormat(user.dateFormat)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const permissions = environment.fakeApis ? (FALLBACK_PERMISSIONS as any) : user.permissions
        this.permissionsService.setPermissions(permissions)
        this.isLoggedInSource.next(true)
      }),
      switchMap(() => (this.termsAgreementNeeded ? this.getAcceptedTerms() : of(null))),
      tap((terms) => {
        terms &&
          this.termsAcceptedSource.next(
            difference(
              ['tos', 'pp'],
              terms.map((term) => term.type),
            ).length === 0,
          )
      }),
      catchError(() => (this.purgeAuth(), of(null))),
      switchMap(() => of(null)),
    )
  }

  getImpersonation(): Observable<AuthResponse> | Observable<null> {
    const clientId = +sessionStorage.getItem('clientId')
    const userId = +sessionStorage.getItem('userId')
    const resellerId = +sessionStorage.getItem('resellerId')

    if (clientId) {
      return this.getClientImpersonation(clientId)
    } else if (userId) {
      return this.getUserImpersonation(userId)
    } else if (resellerId) {
      return this.getResellerImpersonation(resellerId)
    }

    return of(null)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  login(email: string, password: string, domain?: string): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.apiService.post('/login', { email, password, domain }).pipe(map((resp) => resp['data']))
  }

  getUser(fields?: (keyof User)[]) {
    return (
      this.apiService
        .get('/me', {
          params: {
            ...(fields && { ['fields[]']: fields }),
          },
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .pipe(map((resp) => new User(resp['data'])))
    )
  }

  verifyOtp(email: string, code: string): Observable<AuthResponse> {
    return this.apiService.post('/login/otp', { email, code })
  }

  setAuth(authResponse: AuthResponse): Observable<LoginResponse> {
    this.jwtService.saveToken(authResponse.token)
    this.isRememberMe && this.jwtService.saveRefreshToken(authResponse.refreshToken)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.populate()
  }

  purgeAuth() {
    this.setCurrentUser(null)
    this.setCurrentUserDateFormat(null)
    this.setCurrentUserBlockingMessage(null)
    this.isLoggedIn = false
    this.isLoggedInSource.next(false)
    this.jwtService.destroyToken()
    this.jwtService.destroyRefreshToken()
    this.permissionsService.purge()
    this.isImpersonatedSource.next(false)
    localStorage.removeItem('stateTables')
    sessionStorage.removeItem('stateTables')
  }

  setCurrentUser(user: User | null) {
    this.setDefaultDayjsTZOffset(user)
    this.currentUserSource.next(user)
  }

  setDefaultDayjsTZOffset(user: User | null) {
    // Defensive: BE Go may return `timezone: null` (spec A-005). The User
    // constructor backfills a fallback when `environment.fakeApis` is on, but
    // in prod (fakeApis=false) timezone may still be null — don't crash.
    if (!user?.timezone?.name) {
      return
    }
    dayjs.tz.setDefault(user.timezone.name)
  }

  setCurrentUserEmail(email: string) {
    this.currentUserEmail = email
  }

  setCurrentUserBlockingMessage(message: string) {
    this.currentUserBlockingMessage = message
  }

  setCurrentUserPhone(phone: string) {
    this.currentUserPhone = phone
  }

  setIsRememberMe(isRememberMe: boolean) {
    this.isRememberMe = isRememberMe
  }

  getCurrentUser() {
    return this.currentUserSource.getValue()
  }

  getCurrentUserPhone() {
    return this.currentUserPhone
  }

  getCurrentUserEmail() {
    return this.currentUserEmail
  }

  getCurrentUserBlockingMessage() {
    return this.currentUserBlockingMessage
  }

  getCurrentUserDateFormat(): string {
    return this.currentUserDateFormat
  }

  setCurrentUserDateFormat(dateFormat: string): void {
    this.currentUserDateFormat = dateFormat
  }

  requestNewPassword(email: string, withToast = false) {
    const options = withToast ? { context: withFeedback(null, { onError: false }) } : undefined
    return this.apiService.post('/password/request', { email }, options)
  }

  setNewPassword(token: string, password: string) {
    return this.apiService.post(`/password/set`, { token, password })
  }

  resetPassword(token: string, password: string) {
    return this.apiService.post(`/password/reset`, { token, password })
  }

  checkResetPasswordToken(token: string) {
    return this.apiService.post('/password/check-reset', { token }).pipe(map((resp) => !!resp['tokenValid']))
  }

  checkVerifyUserToken(token: string) {
    return this.apiService.post('/users/check-verify-token', { token }).pipe(map((resp) => !!resp['tokenValid']))
  }

  verifyPhoneNumber(phone: string, code: string): Observable<AuthResponse> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.apiService.post('/phone/verify', { phone, code }).pipe(map((resp) => resp['data']))
  }

  getPhoneNumber(token: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.apiService.post('/phone', { token }).pipe(map((resp) => resp['phone']))
  }

  sendPhone(phone: string, token: string) {
    return this.apiService.post('/phone/code', { phone, token })
  }

  getAcceptedTerms(): Observable<TermOfUse[]> {
    return this.apiService.get('/user-terms')
  }

  checkTermAgreement(name: string): Observable<TermOfUse> {
    return this.apiService.get(`/user-terms/${name}`)
  }

  acceptTerm(name: string): Observable<TermOfUse> {
    return this.apiService.post(`/user-terms/${name}`, {})
  }

  getClientImpersonation(id: number): Observable<AuthResponse> {
    return this.apiService.post(`/client/${id}/login`, null)
  }

  getUserImpersonation(id: number): Observable<AuthResponse> {
    return this.apiService.post(`/user/${id}/login`, null)
  }

  getResellerImpersonation(id: number): Observable<AuthResponse> {
    return this.apiService.post(`/reseller/${id}/login`, null)
  }

  refreshToken(refreshToken: string): Observable<AuthResponse> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-explicit-any
    return (this.apiService.post('/token/refresh', { refreshToken }) as any).pipe(
      tap((authResponse: AuthResponse) => this.jwtService.saveToken(authResponse.token)),
    )
  }

  logout(): Observable<LogoutResponse> {
    this.goRealtime.disconnect()
    return this.apiService.post('/logout', {})
  }

  impersonateAsUser(userId: number) {
    sessionStorage.setItem('userId', String(userId))
    open('/', '_blank')
    sessionStorage.removeItem('userId')
  }

  impersonateAsClient(clientId, routerLink?: string) {
    sessionStorage.setItem('clientId', String(clientId))
    open(routerLink || '/', '_blank')
    sessionStorage.removeItem('clientId')
  }

  impersonateAsReseller(resellerId, routerLink?: string) {
    sessionStorage.setItem('resellerId', String(resellerId))
    open(routerLink || '/', '_blank')
    sessionStorage.removeItem('resellerId')
  }

  ///for banner
  shouldShowGlobalBanner(): boolean {
    return false
  }

  fetchCrispCompanyStats(): Observable<{ data: unknown }> {
    const url = '/me'
    const params = {
      'fields[]': [
        'team.companyCreateDate',
        'team.companyDevicesTotal',
        'team.companyVehiclesTotal',
        'team.companyUsersTotal',
        'team.deviceTypes',
      ],
    }

    return this.apiService.get(url, { params })
  }
}
